export enum InvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
}

export interface InvoiceItem {
  id: string;
  invoiceId: string;
  placementId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  live_url?: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  month: number;
  year: number;
  subtotal: number;
  totalAmount: number;
  total: number;
  overrideAmount: number | null;
  overrideReason: string | null;
  finalAmount: number;
  status: InvoiceStatus;
  generatedAt: string;
  sentAt: string | null;
  paidAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  client?: {
    id: string;
    name: string;
    email: string;
  };
  items?: InvoiceItem[];
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface ClientWithPlacements {
  id: string;
  name: string;
  email: string;
  placementCount: number;
  totalAmount: number;
}

export interface ProjectWithPlacements {
  id: string;
  projectName: string;
  placementCount: number;
  totalAmount: number;
}

export interface GenerateInvoiceRequest {
  clientId: string;
  month: number;
  year: number;
}

export interface OverrideAmountRequest {
  overrideAmount: number;
}

export interface UpdateStatusRequest {
  status: InvoiceStatus;
}

export interface InvoiceFilters {
  clientId?: string;
  projectId?: string;
  month?: number;
  year?: number;
  status?: InvoiceStatus;
}

// Status options for dropdowns
export const INVOICE_STATUS_OPTIONS = [
  { value: InvoiceStatus.DRAFT, label: 'Draft', color: 'default' },
  { value: InvoiceStatus.SENT, label: 'Sent', color: 'info' },
  { value: InvoiceStatus.PAID, label: 'Paid', color: 'success' },
  { value: InvoiceStatus.OVERDUE, label: 'Overdue', color: 'error' },
  { value: InvoiceStatus.CANCELLED, label: 'Cancelled', color: 'warning' },
] as const;

// Month options for dropdowns
export const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

// Helper function to get month name
export function getMonthName(month: number): string {
  const monthOption = MONTH_OPTIONS.find(m => m.value === month);
  return monthOption ? monthOption.label : '';
}

// Helper function to format invoice amount
export function formatInvoiceAmount(amount: number | string | any): string {
  // Convert to number if it's a string or Prisma Decimal
  const numAmount = typeof amount === 'number' ? amount : parseFloat(String(amount));
  
  // Check if it's a valid number
  if (isNaN(numAmount)) {
    return '$0.00';
  }
  
  return `$${numAmount.toFixed(2)}`;
}
